import classNames from "classnames";
import moment, { Moment } from "moment";
import { RootState } from "redux/store";
import { useSelector } from "react-redux";
import { offersApi } from "redux/apis";
import Countdown from "components/CountdownV2";
import { timeStyle } from "./styles";
import { useEnvContext } from "contexts/EnvProvider";
import { Fragment, useEffect, useMemo, useState } from "react";
import { useInitialActions, useListeners } from "./hooks";
import { FeaturesFlags } from "helpers";
import { getVariantImage } from "helpers/getVariantImage";
import { useLinks } from "hooks/useLinks";
import { useWebStoreContext } from "contexts/WebStoreProvider";
import useDIPCalculateScale from "./hooks/useDIPCalculateScale";
import { useAppDispatch, useAppSelector, useImagePreloader } from "hooks";
import {
	getDIPs,
	onCloseDIP,
	onDIPtrigger,
	selectDipState
} from "redux/slices/dip";
import {
	DIPS_IMAGE_VARIANT_TAG,
	getClassNameVariant,
	getCloseClickTrackingParams,
	getDipAnalyticsType
} from "./helper";
import { useGoogleAnalyticsContext } from "contexts/GoogleAnalyticsProvider";
import {
	Animated,
	Assets,
	Bogo,
	BuyItAll,
	Close,
	ClubGift,
	Container,
	Informative,
	Offers,
	Wrapper
} from ".";
import { CountdownOffer } from "./lib/CountdownOffer";
import CountdownV3 from "components/CountdownV3";
import { useMediaQuery } from "@mui/material";
import { DataOverlay } from "./lib/DataOverlay";
import { Dip } from "interfaces";
import { selectCommonState, selectPopUpManagerState } from "redux/slices";

const DIP = () => {
	const links = useLinks();
	const dispatch = useAppDispatch();
	const { isFeatureFlagEnabled } = useEnvContext();
	const { nowTime, authToken } = useWebStoreContext();
	const { variant } = useAppSelector(selectPopUpManagerState);
	const { tutorialSlideNumber } = useAppSelector(selectCommonState);
	const isLandscape = useMediaQuery("(orientation: landscape)");
	const {
		dipType,
		opened,
		fromHud,
		activeTrigger,
		hudTimerExpirationDate,
		currentDip
	} = useAppSelector(selectDipState);
	const showOfferId = useSelector(
		(state: RootState) => state.debug.showDebugLabels
	);
	const { reportEvent, reportPlaygamiEvent } = useGoogleAnalyticsContext();
	const [dipEndTime, setDipEndTime] = useState<Moment | undefined | string>();
	const [stickyTimerLabel, setStickyTimerLabel] = useState<string | undefined>(
		undefined
	);
	const [lastDip, setLastDip] = useState<Dip | undefined>(undefined);

	const variantUrl = useMemo(() => {
		return getVariantImage(
			currentDip?.dip.imageUrl as string,
			DIPS_IMAGE_VARIANT_TAG,
			isLandscape ? "large" : "small"
		);
	}, [currentDip?.dip.imageUrl, isLandscape]);

	// prettier-ignore
	const { isImageLoaded: isBackgroundImageLoaded } = useImagePreloader(variantUrl);
	// prettier-ignore
	const { isImageLoaded: isHeaderImageLoaded } = useImagePreloader(variantUrl);
	// prettier-ignore
	const isStickyDip = typeof currentDip?.dip.timerDurationMinutes !== "undefined";
	const isOffersType = dipType === "offers";
	const hasHudTimerExpirationDate =
		fromHud && currentDip?.dip.timer === true && !!hudTimerExpirationDate;
	const showCountdown =
		currentDip?.dip.timer || isStickyDip || hasHudTimerExpirationDate;
	const isBogo =
		currentDip?.dip?.isBogo || currentDip?.dip?.templateType === "Bogo";
	const isBuyItAll =
		currentDip?.dip?.isBuyItAll || currentDip?.dip?.templateType === "BuyItAll";
	const isClubGift = currentDip?.dip?.templateType === "Galore";
	const isCountdownOffer = currentDip?.dip?.templateType === "Countdown";
	const isBouncyChest = currentDip?.dip?.triggerType === "bouncyChestClick";
	const scale = useDIPCalculateScale(dipType);

	const isTutorialActive = useMemo(() => {
		return tutorialSlideNumber === 3 && variant === "tutorial-loyalty-shop";
	}, [tutorialSlideNumber, variant]);

	const { isLoading: isPurchaseOffersLoading } =
		offersApi.endpoints.getOffers.useQueryState({
			offerType: "singleItem",
			isAuthenticated: !!authToken?.value
		});

	const { isLoading: isLoyaltyOffersloading } =
		offersApi.endpoints.getOffers.useQueryState({
			offerType: "loyalty",
			isAuthenticated: !!authToken?.value
		});

	useInitialActions();
	useListeners();

	const handleCloseClick = () => {
		dispatch(onCloseDIP());
		setDipEndTime(undefined);
		setStickyTimerLabel(undefined);
		setTimeout(() => {
			dispatch(onDIPtrigger({ triggerType: "cache-mode" }));
		}, 1000);
		reportEvent("dialog_x_close", {
			source: "dips_dialog",
			...getCloseClickTrackingParams(currentDip?.dip.id, currentDip?.buttons)
		});
		reportPlaygamiEvent("tp.web_frontend", {
			event_name: fromHud ? "limited_deal_hud_click" : "dialog_x_close",
			event_type: fromHud ? "dips_dialog" : "dialog_x_close",
			action: fromHud ? "dialog_x_close" : "click",
			source: "dips_dialog",
			event_id: currentDip?.dip.id,
			offer_id: currentDip?.buttons?.map((b) => b.offerId).join(", "),
			type: getDipAnalyticsType({
				fromHud,
				activeTrigger,
				pathname: location.pathname,
				shopPath: links.shop.path,
				eliteDealsPath: links.elite_deals.path
			}),
			page_location: location.pathname
		});
	};

	useEffect(() => {
		if (currentDip) {
			setLastDip(currentDip);
		}
	}, [currentDip]);

	//* This effect is used to refetch DIPs after the cooldown period of the last dip.
	useEffect(() => {
		if (!currentDip && lastDip?.dip?.cooldownMinutes) {
			setTimeout(() => {
				dispatch(getDIPs());
			}, lastDip?.dip?.cooldownMinutes * 60 * 1000);
		}
	}, [currentDip]);

	useEffect(() => {
		if (!currentDip) {
			setDipEndTime(undefined);
			return;
		}

		if (typeof dipEndTime !== "undefined") {
			return;
		}
		setDipEndTime(
			fromHud
				? hudTimerExpirationDate
				: isStickyDip
				? currentDip.timerExpirationDate ??
				  moment(nowTime).add(currentDip.dip.timerDurationMinutes, "minutes")
				: currentDip?.dip.endTime
		);
	}, [
		dipEndTime,
		nowTime,
		isStickyDip,
		currentDip,
		fromHud,
		hudTimerExpirationDate
	]);

	return opened &&
		isFeatureFlagEnabled(FeaturesFlags.DIPS) &&
		isBackgroundImageLoaded &&
		!isLoyaltyOffersloading &&
		!isPurchaseOffersLoading &&
		isHeaderImageLoaded ? (
		<Wrapper isTutorialActive={isTutorialActive}>
			<Animated>
				<Container
					scale={scale}
					id={currentDip?.dip.id}
					isTutorialActive={isTutorialActive}
					className={classNames([
						isOffersType ? "offers" : "informative",
						isBogo && "bogo",
						isBuyItAll && "buy-it-all",
						isClubGift && "galore",
						getClassNameVariant(currentDip?.buttons?.length as number)
					])}
				>
					{showOfferId && nowTime && currentDip && (
						<DataOverlay
							nowTime={nowTime}
							currentDip={currentDip}
							fromHud={fromHud}
						/>
					)}
					{isBuyItAll ? (
						<BuyItAll />
					) : isBogo ? (
						<Bogo />
					) : isClubGift ? (
						<ClubGift />
					) : isCountdownOffer ? (
						<CountdownOffer />
					) : isOffersType ? (
						<Offers />
					) : (
						<Informative />
					)}
					<Assets dipType={dipType} variantUrl={variantUrl} />
					{!currentDip?.dip?.suppressCloseButton && (
						<Close onClose={handleCloseClick} />
					)}
					{showCountdown &&
						dipEndTime &&
						!isCountdownOffer &&
						!isBouncyChest && (
							<Countdown
								// @ts-ignore
								endTime={dipEndTime}
								startTime={nowTime}
								text={stickyTimerLabel}
								className="dip-countdown"
								onEnd={() => {
									if (isStickyDip) {
										setStickyTimerLabel("Expired");
										return;
									}
									dispatch(onCloseDIP());
									setDipEndTime(undefined);
								}}
								hideTimerDisplayOnEnd={true}
								style={timeStyle}
							/>
						)}
					{isCountdownOffer && showCountdown && dipEndTime && (
						<CountdownV3
							// @ts-ignore
							endTime={dipEndTime}
							startTime={nowTime}
							text={"Offer ends in:"}
							className="dip-countdown"
							onEnd={() => {
								dispatch(onCloseDIP());
								setDipEndTime(undefined);
							}}
							//hideTimerDisplayOnEnd={true}
							style={{
								position: "absolute",
								bottom: isLandscape ? "15%" : "unset",
								top: isLandscape ? "unset" : "28%",
								left: isLandscape ? "4.2%" : "unset",
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
								flexDirection: "column",
								gap: "0.5rem",
								fontSize: isLandscape ? "4.9em" : "4.65em"
							}}
							labelStyle={{
								WebkitTextStrokeWidth: "0.22em",
								WebkitTextStrokeColor: "#000",
								paintOrder: "stroke fill",
								color: "#fff",
								fontWeight: 400,
								fontSize: "0.35em",
								fontFamily: "Baloo",
								lineHeight: "1.2em"
							}}
						/>
					)}
				</Container>
			</Animated>
		</Wrapper>
	) : (
		<Fragment />
	);
};

export default DIP;
export * from "./types/provider";
export * from "./animation";
export * from "./styles";
export * from "./lib";
